import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import QRCode from 'qrcode';
import Api from '../Api';

// Logo path (relative to public folder)
const LOGO_PATH = '/images/logos/iya-nuevologo.png';

// Professional data (fixed)
const PROFESSIONAL = {
  name: 'GONZALO C. SERRANO',
  title: 'ING. INDUSTRIAL',
  specialty: 'ESP. EN HIGIENE Y SEGURIDAD',
  matCipba: 'Mat. CIPBA N°51.458',
  matNacCpii: 'Mat. Nac. CPII N°4500',
  ehs: 'EHS 007694 PBA',
  regCipba: 'REG. CIPBA HyS N°1209',
  matAsp: 'Mat. ASP N°484'
};

// Month names in Spanish
const MONTH_NAMES = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Helper to load image from URL
const loadImageFromUrl = async (url) => {
  const response = await fetch(url);
  return await response.arrayBuffer();
};

// Generate the certification PDF document
const generateCertificationPdf = async (reportData) => {
  const pdfDoc = await PDFDocument.create();

  // Embed fonts
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Load and embed logo
  let logoImage;
  try {
    const logoBytes = await loadImageFromUrl(LOGO_PATH);
    logoImage = await pdfDoc.embedPng(logoBytes);
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  // Add page
  const page = pdfDoc.addPage([595, 842]); // A4 size
  const { width, height } = page.getSize();

  let yPosition = height - 50;

  // Draw logo (centered at top)
  if (logoImage) {
    const logoWidth = 200;
    const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
    page.drawImage(logoImage, {
      x: (width - logoWidth) / 2,
      y: yPosition - logoHeight,
      width: logoWidth,
      height: logoHeight,
    });
    yPosition -= logoHeight + 40;
  }

  // Title
  const title = 'CERTIFICADO DE INFORME';
  const titleFontSize = 22;
  const titleWidth = fontBold.widthOfTextAtSize(title, titleFontSize);
  page.drawText(title, {
    x: (width - titleWidth) / 2,
    y: yPosition,
    size: titleFontSize,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  yPosition -= 50;

  // Horizontal line
  page.drawLine({
    start: { x: 50, y: yPosition },
    end: { x: width - 50, y: yPosition },
    thickness: 1,
    color: rgb(0.7, 0.7, 0.7),
  });
  yPosition -= 40;

  // Report data section
  const labelFontSize = 12;
  const valueFontSize = 14;
  const lineHeight = 30;

  // Month name for emission date
  const monthName = MONTH_NAMES[reportData.month] || '';

  // Client
  page.drawText('Cliente:', {
    x: 50,
    y: yPosition,
    size: labelFontSize,
    font: fontBold,
    color: rgb(0.3, 0.3, 0.3),
  });
  page.drawText(reportData.clientName || '-', {
    x: 130,
    y: yPosition,
    size: valueFontSize,
    font: font,
    color: rgb(0, 0, 0),
  });
  yPosition -= lineHeight;

  // Plant (if exists)
  if (reportData.plantName) {
    page.drawText('Planta:', {
      x: 50,
      y: yPosition,
      size: labelFontSize,
      font: fontBold,
      color: rgb(0.3, 0.3, 0.3),
    });
    page.drawText(reportData.plantName, {
      x: 130,
      y: yPosition,
      size: valueFontSize,
      font: font,
      color: rgb(0, 0, 0),
    });
    yPosition -= lineHeight;
  }

  // Service
  page.drawText('Servicio:', {
    x: 50,
    y: yPosition,
    size: labelFontSize,
    font: fontBold,
    color: rgb(0.3, 0.3, 0.3),
  });
  page.drawText(reportData.serviceName || '-', {
    x: 130,
    y: yPosition,
    size: valueFontSize,
    font: font,
    color: rgb(0, 0, 0),
  });
  yPosition -= lineHeight;

  // Emission date
  page.drawText('Fecha de Emisión:', {
    x: 50,
    y: yPosition,
    size: labelFontSize,
    font: fontBold,
    color: rgb(0.3, 0.3, 0.3),
  });
  page.drawText(`${monthName} ${reportData.year}`, {
    x: 180,
    y: yPosition,
    size: valueFontSize,
    font: font,
    color: rgb(0, 0, 0),
  });
  yPosition -= 50;

  // Horizontal line
  page.drawLine({
    start: { x: 50, y: yPosition },
    end: { x: width - 50, y: yPosition },
    thickness: 1,
    color: rgb(0.7, 0.7, 0.7),
  });
  yPosition -= 40;

  // Professional section title
  const profTitle = 'PROFESIONAL RESPONSABLE';
  const profTitleWidth = fontBold.widthOfTextAtSize(profTitle, 16);
  page.drawText(profTitle, {
    x: (width - profTitleWidth) / 2,
    y: yPosition,
    size: 16,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  yPosition -= 30;

  // Professional box
  const boxX = 50;
  const boxWidth = width - 100;
  const boxHeight = 160;
  const boxY = yPosition - boxHeight;

  // Draw box border
  page.drawRectangle({
    x: boxX,
    y: boxY,
    width: boxWidth,
    height: boxHeight,
    borderColor: rgb(0.5, 0.5, 0.5),
    borderWidth: 1,
    color: rgb(0.97, 0.97, 0.97),
  });

  // Professional info - centered
  const profInfoFontSize = 12;
  const profLineHeight = 18;
  let profY = boxY + boxHeight - 25;

  // Name (bold)
  const nameWidth = fontBold.widthOfTextAtSize(PROFESSIONAL.name, 14);
  page.drawText(PROFESSIONAL.name, {
    x: (width - nameWidth) / 2,
    y: profY,
    size: 14,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  profY -= profLineHeight;

  // Title
  const profTitleTextWidth = font.widthOfTextAtSize(PROFESSIONAL.title, profInfoFontSize);
  page.drawText(PROFESSIONAL.title, {
    x: (width - profTitleTextWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });
  profY -= profLineHeight;

  // Specialty
  const specialtyWidth = font.widthOfTextAtSize(PROFESSIONAL.specialty, profInfoFontSize);
  page.drawText(PROFESSIONAL.specialty, {
    x: (width - specialtyWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });
  profY -= profLineHeight;

  // Mat CIPBA
  const matCipbaWidth = font.widthOfTextAtSize(PROFESSIONAL.matCipba, profInfoFontSize);
  page.drawText(PROFESSIONAL.matCipba, {
    x: (width - matCipbaWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });
  profY -= profLineHeight;

  // Mat Nac CPII
  const matNacWidth = font.widthOfTextAtSize(PROFESSIONAL.matNacCpii, profInfoFontSize);
  page.drawText(PROFESSIONAL.matNacCpii, {
    x: (width - matNacWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });
  profY -= profLineHeight;

  // EHS
  const ehsWidth = font.widthOfTextAtSize(PROFESSIONAL.ehs, profInfoFontSize);
  page.drawText(PROFESSIONAL.ehs, {
    x: (width - ehsWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });
  profY -= profLineHeight;

  // REG CIPBA HyS
  const regCipbaWidth = font.widthOfTextAtSize(PROFESSIONAL.regCipba, profInfoFontSize);
  page.drawText(PROFESSIONAL.regCipba, {
    x: (width - regCipbaWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });
  profY -= profLineHeight;

  // Mat ASP
  const matAspWidth = font.widthOfTextAtSize(PROFESSIONAL.matAsp, profInfoFontSize);
  page.drawText(PROFESSIONAL.matAsp, {
    x: (width - matAspWidth) / 2,
    y: profY,
    size: profInfoFontSize,
    font: font,
    color: rgb(0.2, 0.2, 0.2),
  });

  // Save PDF
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
};

// Upload certification PDF and get URL
const uploadCertificationPdf = async (pdfBlob, reportData) => {
  const fileName = `certificado-${reportData.clientName || 'informe'}-${reportData.year}-${reportData.month}.pdf`
    .replace(/\s+/g, '-')
    .toLowerCase();

  const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
  const data = new FormData();
  data.append('file', file);

  const response = await Api.post('/files/upload', data);
  return response.data.fileUrl;
};

// Add QR code page to PDF file
const addQrToPdfFile = async (file, qrUrl, logoBytes) => {
  const existingPdfBytes = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  // Embed fonts
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Generate QR code
  const qrDataUrl = await QRCode.toDataURL(qrUrl, {
    width: 200,
    margin: 2,
    color: {
      dark: '#000000',
      light: '#ffffff'
    }
  });
  const qrImageBytes = await fetch(qrDataUrl).then(res => res.arrayBuffer());
  const qrImage = await pdfDoc.embedPng(qrImageBytes);

  // Embed logo if available
  let logoImage;
  if (logoBytes) {
    try {
      logoImage = await pdfDoc.embedPng(logoBytes);
    } catch (error) {
      console.error('Error embedding logo:', error);
    }
  }

  // Add new page
  const page = pdfDoc.addPage();
  const { width, height } = page.getSize();

  let yPosition = height - 50;

  // Draw logo at top (centered)
  if (logoImage) {
    const logoWidth = 180;
    const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
    page.drawImage(logoImage, {
      x: (width - logoWidth) / 2,
      y: yPosition - logoHeight,
      width: logoWidth,
      height: logoHeight,
    });
    yPosition -= logoHeight + 50;
  }

  // Title
  const titleText = 'DOCUMENTO CERTIFICADO';
  const titleFontSize = 20;
  const titleWidth = fontBold.widthOfTextAtSize(titleText, titleFontSize);
  page.drawText(titleText, {
    x: (width - titleWidth) / 2,
    y: yPosition,
    size: titleFontSize,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  yPosition -= 60;

  // QR code (centered)
  const qrSize = 200;
  const qrX = (width - qrSize) / 2;
  page.drawImage(qrImage, {
    x: qrX,
    y: yPosition - qrSize,
    width: qrSize,
    height: qrSize,
  });
  yPosition -= qrSize + 30;

  // Instruction text
  const instructionText = 'Escanee el codigo QR para verificar el certificado';
  const instructionFontSize = 12;
  const instructionWidth = font.widthOfTextAtSize(instructionText, instructionFontSize);
  page.drawText(instructionText, {
    x: (width - instructionWidth) / 2,
    y: yPosition,
    size: instructionFontSize,
    font: font,
    color: rgb(0.3, 0.3, 0.3),
  });

  // Save PDF
  const pdfBytes = await pdfDoc.save();
  const baseName = file.name.replace(/\.pdf$/i, '');
  const newFileName = `${baseName}-certificado.pdf`;
  return new File([pdfBytes], newFileName, { type: 'application/pdf' });
};

// Helper to check if a file is a PDF
export const isPdfFile = (file) => {
  return file && file.name && file.name.toLowerCase().endsWith('.pdf');
};

// Progress status constants
export const PROGRESS_STATUS = {
  GENERATING_CERTIFICATE: 'generating_certificate',
  UPLOADING_CERTIFICATE: 'uploading_certificate',
  ADDING_QR: 'adding_qr',
  DONE: 'done'
};

// Main function to process files with QR
export const processFilesWithQr = async (files, addQr, reportData = null, onProgress = null) => {
  if (!addQr || !reportData) {
    return files;
  }

  try {
    // Step 1: Generate certification PDF
    if (onProgress) onProgress(PROGRESS_STATUS.GENERATING_CERTIFICATE);
    const certificationPdfBlob = await generateCertificationPdf(reportData);

    // Step 2: Upload certification PDF and get URL
    if (onProgress) onProgress(PROGRESS_STATUS.UPLOADING_CERTIFICATE);
    const certificationUrl = await uploadCertificationPdf(certificationPdfBlob, reportData);

    // Step 3: Load logo for QR pages
    let logoBytes;
    try {
      logoBytes = await loadImageFromUrl(LOGO_PATH);
    } catch (error) {
      console.error('Error loading logo for QR page:', error);
    }

    // Step 4: Add QR page to each PDF file
    if (onProgress) onProgress(PROGRESS_STATUS.ADDING_QR);
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        if (isPdfFile(file)) {
          try {
            return await addQrToPdfFile(file, certificationUrl, logoBytes);
          } catch (error) {
            console.error('Error processing PDF:', file.name, error);
            return file;
          }
        }
        return file;
      })
    );

    if (onProgress) onProgress(PROGRESS_STATUS.DONE);
    return processedFiles;
  } catch (error) {
    console.error('Error in processFilesWithQr:', error);
    return files;
  }
};
